'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { COOKIE_KEY, type TotalPricePreference } from '@/app/common/cookies/types.ts';
import { setCookie } from '@/app/common/cookies/clientCookies.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { createPriceToggleSelectedPayload } from '@/app/common/starTrack/events/priceToggleSelected.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';

type PriceModeContextType = {
  totalPricePreference: TotalPricePreference | null;
  // Every toggle writes through this, so the checked/preference mapping lives in one place.
  setShowTotalPrice: (showTotalPrice: boolean) => void;
};

export const PriceModeContext = createContext<PriceModeContextType>({
  totalPricePreference: null,
  setShowTotalPrice: () => undefined,
});

const TOTAL_PRICE_COOKIE_EXPIRE_DAYS = 30;

export default function PriceModeProvider({
  initialPreference,
  children,
}: {
  initialPreference: TotalPricePreference | undefined;
  children: React.ReactNode;
}) {
  const [totalPricePreference, setPreference] = useState<TotalPricePreference | null>(initialPreference ?? null);
  const { starTrack } = useCommonClient();
  const { searchInfoData } = usePageWrapperData();
  // pageType is read from the URL because it is the only source kept in sync while navigating the
  // funnel client-side; searchId falls back to the server value, which is never missing.
  const searchId = searchInfoData?.searchId || '';

  const setShowTotalPrice = useCallback(
    (showTotalPrice: boolean) => {
      const preference: TotalPricePreference = showTotalPrice ? 'total' : 'pp';
      setPreference(preference);
      setCookie(COOKIE_KEY.TOTAL_PRICE_EVOLUTION, preference, TOTAL_PRICE_COOKIE_EXPIRE_DAYS);
      starTrack.priceToggleSelected(
        createPriceToggleSelectedPayload({ searchId, searchParams: getSearchParams(), showTotalPrice })
      );
    },
    [starTrack, searchId]
  );

  const value = useMemo(() => ({ totalPricePreference, setShowTotalPrice }), [totalPricePreference, setShowTotalPrice]);

  return <PriceModeContext.Provider value={value}>{children}</PriceModeContext.Provider>;
}

export const usePriceModePreference = () => useContext(PriceModeContext);
